// Event Bus - Central communication system
import type { EventBus } from "../types/index.ts";

/**
 * Creates an event bus for component communication
 * @returns EventBus API with emit, on, off, and destroy methods
 */
export const createEventBus = (): EventBus => {
  // Private state - event listeners map
  const listeners = new Map<string, Set<Function>>();

  const emit = <T>(event: string, data: T): void => {
    const eventListeners = listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`EventBus: Error in listener for "${event}":`, error);
        }
      });
    }
  };

  const on = <T>(event: string, callback: (data: T) => void): (() => void) => {
    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }

    const eventListeners = listeners.get(event)!;
    eventListeners.add(callback);

    // Return unsubscribe function
    return () => {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        listeners.delete(event);
      }
    };
  };

  const off = (event: string, callback: Function): void => {
    const eventListeners = listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        listeners.delete(event);
      }
    }
  };

  const destroy = (): void => {
    listeners.clear();
  };

  // Public API
  return {
    emit,
    on,
    off,
    destroy,
  };
};
